import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaBoxOpen,
  FaMapMarkerAlt,
  FaSignOutAlt,
  FaCamera,
  FaSpinner,
  FaEdit,
  FaSave,
  FaTimes,
  FaPhone,
  FaEnvelope,
  FaChevronRight,
  FaCalendarAlt,
  FaPlus,
  FaTicketAlt,
  FaCopy,
  FaHeartbeat // Using Heartbeat icon for health checkup
} from "react-icons/fa";
import Swal from "sweetalert2";
import SummaryApi from "../common";

const Profile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // --- ADDRESS MODAL STATE ---
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressForm, setAddressForm] = useState({
    flat_number: "",
    street_name: "",
    land_mark: "",
    city: "",
    state: "",
    pincode: ""
  });

  const [userData, setUserData] = useState({
    full_name: "",
    mobile_number: "",
    email: "",
    profile_img: "",
  });

  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);

  // --- SINGLE COUPON DATA ---
  const [coupons] = useState([
    { 
        id: 1, 
        code: "FIRSTCARE", 
        offer: "Free Health Checkup", 
        desc: "Exclusive offer on your first visit/order only.", 
        expiry: "2025-12-31", 
        color: "from-teal-500 to-teal-600",
        bg: "bg-teal-50",
        text: "text-teal-700"
    }
  ]);

  const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("user_id");

  // --- HELPERS ---
  const displayCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  };

  const getProductImage = (imgPath) => {
    if (!imgPath) return "https://via.placeholder.com/150";
    const domain = SummaryApi.backendDomain.replace(/\/$/, "");
    const cleanPath = imgPath.replace(/^\//, "");
    return `${domain}/${cleanPath}`;
  };

  // --- INITIALIZATION ---
  useEffect(() => {
    if (!customerId) {
      navigate("/login");
      return;
    }
    fetchProfileData();
  }, [customerId, navigate]);

  useEffect(() => {
    if (activeTab === "orders" && customerId) {
      fetchOrders();
    }
  }, [activeTab, customerId]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const userResponse = await fetch(SummaryApi.customerDetailsById.url, {
        method: SummaryApi.customerDetailsById.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const userJson = await userResponse.json();

      if (userJson.status && userJson.data) {
        const details = Array.isArray(userJson.data) ? userJson.data[0] : userJson.data;
        setUserData({
          full_name: details.full_name || details.customer_name || "",
          mobile_number: details.mobile_number || details.customer_mobile || "",
          email: details.email || "",
          profile_img: details.profile_img || "",
        });
      }

      await fetchAddresses();

    } catch (error) {
      console.error("Profile fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const addrResponse = await fetch(SummaryApi.getAddressById.url, {
        method: SummaryApi.getAddressById.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const addrJson = await addrResponse.json();
      if (addrJson.status && Array.isArray(addrJson.data)) {
        setAddresses(addrJson.data);
      }
    } catch (error) {
      console.error("Address fetch error", error);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const response = await fetch(SummaryApi.order.getById.url, {
        method: SummaryApi.order.getById.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({ customer_id: customerId }),
      });

      const data = await response.json();

      if (data.status && Array.isArray(data.data)) {
        const sortedOrders = data.data.sort(
          (a, b) => new Date(b.order_date_time) - new Date(a.order_date_time)
        );
        setOrders(sortedOrders);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  // --- COPY COUPON HANDLER ---
  const handleCopyCoupon = (code) => {
    navigator.clipboard.writeText(code);
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true
    });
    Toast.fire({
        icon: 'success',
        title: 'Coupon Copied!'
    });
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to sign out?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#111827",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Logout",
    }).then((result) => {
      if (result.isConfirmed) {
        sessionStorage.clear();
        localStorage.clear();
        navigate("/login");
      }
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setIsEditing(false);
      Swal.fire({
        icon: "success",
        title: "Profile Updated",
        text: "Your details have been saved successfully.",
        confirmButtonColor: "#111827",
      });
    }, 1000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    if(name === 'pincode') {
        if(/^\d*$/.test(value) && value.length <= 6) {
             setAddressForm(prev => ({ ...prev, [name]: value }));
        }
    } else {
        setAddressForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if(!addressForm.flat_number || !addressForm.street_name || !addressForm.city || !addressForm.state || !addressForm.pincode) {
        Swal.fire('Missing Fields', 'Please fill all required fields', 'warning');
        return;
    }
    try {
        setAddressLoading(true);
        const fullAddrStr = `${addressForm.flat_number}, ${addressForm.street_name}, ${addressForm.land_mark}, ${addressForm.city}, ${addressForm.state} - ${addressForm.pincode}`;
        const response = await fetch(SummaryApi.addAddress.url, {
            method: SummaryApi.addAddress.method,
            headers: {
                "Content-Type": "application/json",
                Authorization: SummaryApi.bearerToken,
            },
            body: JSON.stringify({
                customer_id: customerId,
                customer_name: userData.full_name,
                customer_mobile: userData.mobile_number, 
                flat_number: addressForm.flat_number,
                street_name: addressForm.street_name,
                land_mark: addressForm.land_mark,
                city: addressForm.city,
                state: addressForm.state,
                pincode: addressForm.pincode,
                address: fullAddrStr,
                latitude: "",
                longitude: ""
            })
        });
        const data = await response.json();
        if(data.status) {
            Swal.fire('Success', 'Address added successfully', 'success');
            setAddressForm({ flat_number: "", street_name: "", land_mark: "", city: "", state: "", pincode: "" });
            setShowAddressModal(false);
            fetchAddresses(); 
        } else {
            Swal.fire('Error', data.message || 'Failed to add address', 'error');
        }
    } catch (error) {
        Swal.fire('Error', 'Something went wrong', 'error');
    } finally {
        setAddressLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("delivered")) return "bg-green-100 text-green-700 border-green-200";
    if (s.includes("cancel")) return "bg-red-100 text-red-700 border-red-200";
    if (s.includes("ship")) return "bg-blue-100 text-blue-700 border-blue-200";
    return "bg-yellow-100 text-yellow-700 border-yellow-200";
  };

  if (loading && !userData.full_name) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><FaSpinner className="animate-spin text-4xl text-blue-600" /></div>;
  }

  // --- REUSABLE COMPONENTS ---
  const SidebarItem = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm font-medium transition-all rounded-xl mb-1
      ${activeTab === id ? "bg-gray-900 text-white shadow-md" : "text-gray-500 hover:bg-white hover:text-gray-900"}`}
    >
      <Icon size={16} />
      {label}
    </button>
  );

  const InputField = ({ label, name, value, onChange, icon: Icon, type = "text", readOnly = false }) => (
    <div className="mb-5">
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{label}</label>
      <div className="relative">
        {Icon && (<div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400"><Icon /></div>)}
        <input
          type={type} name={name} value={value} onChange={onChange} readOnly={readOnly}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 rounded-lg border text-sm transition-all outline-none
          ${readOnly ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed" : "bg-white border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"}`}
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6] py-8 lg:py-12 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">My Account</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* SIDEBAR */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col items-center text-center mb-6">
              <div className="relative mb-4">
                <div className="w-24 h-24 rounded-full bg-gray-100 overflow-hidden border-4 border-white shadow-sm">
                  {userData.profile_img ? (
                    <img src={userData.profile_img} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300"><FaUser size={40} /></div>
                  )}
                </div>
                <button className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-md transition"><FaCamera size={12} /></button>
              </div>
              <h2 className="text-lg font-bold text-gray-900">{userData.full_name || "Guest User"}</h2>
              <p className="text-sm text-gray-500">{userData.email || "No email provided"}</p>
            </div>

            <nav className="space-y-1">
              <SidebarItem id="profile" icon={FaUser} label="Personal Information" />
              <SidebarItem id="orders" icon={FaBoxOpen} label="My Orders" />
              {/* NEW COUPONS TAB */}
              <SidebarItem id="coupons" icon={FaTicketAlt} label="My Coupons" />
              <SidebarItem id="address" icon={FaMapMarkerAlt} label="Manage Addresses" />
              <div className="pt-4 mt-4 border-t border-gray-200">
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-all">
                  <FaSignOutAlt size={16} /> Logout
                </button>
              </div>
            </nav>
          </div>

          {/* MAIN CONTENT */}
          <div className="lg:w-3/4">
            
            {/* PROFILE TAB */}
            {activeTab === "profile" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                  <h3 className="font-bold text-gray-800">Profile Details</h3>
                  <button onClick={() => setIsEditing(!isEditing)} className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${isEditing ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-blue-50 text-blue-600 hover:bg-blue-100"}`}>
                    {isEditing ? <><FaTimes /> Cancel</> : <><FaEdit /> Edit Profile</>}
                  </button>
                </div>
                <div className="p-6 lg:p-8">
                  <form onSubmit={handleUpdateProfile}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <InputField label="Full Name" name="full_name" value={userData.full_name} onChange={handleInputChange} icon={FaUser} readOnly={!isEditing} />
                      <InputField label="Email Address" name="email" value={userData.email} onChange={handleInputChange} icon={FaEnvelope} type="email" readOnly={!isEditing} />
                      <div className="md:col-span-2">
                        <InputField label="Mobile Number" name="mobile_number" value={userData.mobile_number} onChange={handleInputChange} icon={FaPhone} readOnly={true} />
                        <p className="text-xs text-gray-400 -mt-3 ml-1">Mobile number cannot be changed for security reasons.</p>
                      </div>
                    </div>
                    {isEditing && (
                      <div className="mt-8 flex justify-end">
                        <button type="submit" disabled={loading} className="bg-gray-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-gray-800 shadow-lg flex items-center gap-2">
                          {loading ? <FaSpinner className="animate-spin" /> : <><FaSave /> Save Changes</>}
                        </button>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            )}

            {/* ORDERS TAB */}
            {activeTab === "orders" && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Order History</h3>
                {ordersLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <FaSpinner className="animate-spin text-3xl text-gray-400 mb-3" />
                    <p className="text-gray-500">Loading your orders...</p>
                  </div>
                ) : orders.length > 0 ? (
                  orders.map((order) => {
                    const firstItem = order.order_items?.[0];
                    const itemCount = order.order_items?.length || 0;
                    const imgPath = firstItem?.product_img;
                    return (
                      <div key={order.order_id} onClick={() => navigate(`/order/${order.order_id}`)} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group cursor-pointer">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                          <div className="flex gap-4">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-50 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                              <img src={getProductImage(imgPath)} alt={firstItem?.product_name} className="w-full h-full object-contain mix-blend-multiply" onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=No+Image"; }} />
                            </div>
                            <div>
                              <div className="flex items-center gap-3 mb-1">
                                <span className="font-bold text-gray-900 text-base">Order #{order.order_code}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide ${getStatusColor(order.order_status)}`}>{order.order_status}</span>
                              </div>
                              <p className="text-sm text-gray-500 flex items-center gap-1 mb-1"><FaCalendarAlt size={12} /> {formatDate(order.order_date_time)}</p>
                              <p className="text-xs text-gray-500">{firstItem?.product_name} {itemCount > 1 && <span className="text-blue-600 font-medium">+{itemCount - 1} more items</span>}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between sm:flex-col sm:items-end sm:justify-center gap-2 mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-0 border-gray-100 w-full sm:w-auto">
                            <div>
                                <p className="text-xs text-gray-400 sm:text-right">Total Amount</p>
                                <p className="font-bold text-gray-900 text-lg">{displayCurrency(order.total_amount)}</p>
                            </div>
                            <div className="hidden sm:block text-gray-300 group-hover:text-blue-600 transition-colors mt-2"><FaChevronRight /></div>
                            <FaChevronRight className="sm:hidden text-gray-300" />
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                    <FaBoxOpen className="mx-auto text-gray-300 text-5xl mb-3" />
                    <p className="text-gray-500">No orders found.</p>
                    <button onClick={() => navigate("/product")} className="mt-4 text-blue-600 font-semibold hover:underline">Start Shopping</button>
                  </div>
                )}
              </div>
            )}
            
            {/* COUPONS TAB (UPDATED SINGLE COUPON) */}
            {activeTab === "coupons" && (
                <div>
                     <h3 className="text-lg font-bold text-gray-900 mb-6">Available Coupons</h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {coupons.map((coupon) => (
                            <div key={coupon.id} className="relative bg-white rounded-xl shadow-sm border border-gray-200 flex overflow-hidden group hover:shadow-md transition-shadow">
                                {/* Left Color Bar */}
                                <div className={`w-3 bg-gradient-to-b ${coupon.color}`}></div>
                                
                                {/* Content */}
                                <div className="flex-1 p-5 pr-8">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FaHeartbeat className={coupon.text} size={16} />
                                        <span className={`text-xs font-bold uppercase ${coupon.text} bg-opacity-10 px-2 py-0.5 rounded-full ${coupon.bg}`}>
                                            New Member
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">{coupon.offer}</h3>
                                    <p className="text-xs text-gray-500 leading-relaxed mb-3">{coupon.desc}</p>
                                    <p className="text-[10px] text-gray-400">Expires on: {formatDate(coupon.expiry)}</p>
                                </div>

                                {/* Divider Logic (Circles for ticket effect) */}
                                <div className="relative w-0 border-l-2 border-dashed border-gray-200 my-2">
                                    <div className="absolute -top-5 -left-3 w-6 h-6 bg-[#F3F4F6] rounded-full"></div>
                                    <div className="absolute -bottom-5 -left-3 w-6 h-6 bg-[#F3F4F6] rounded-full"></div>
                                </div>

                                {/* Code Section */}
                                <div className="w-28 bg-gray-50 flex flex-col items-center justify-center p-2 relative">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 rotate-0">Code</p>
                                    <h4 className="font-mono font-bold text-gray-800 text-base mb-2 select-all">{coupon.code}</h4>
                                    <button 
                                        onClick={() => handleCopyCoupon(coupon.code)}
                                        className="text-xs font-bold text-teal-600 hover:bg-teal-100 p-2 rounded-full transition-colors flex items-center gap-1"
                                    >
                                        <FaCopy /> Copy
                                    </button>
                                </div>
                            </div>
                        ))}
                     </div>
                </div>
            )}

            {/* ADDRESS TAB */}
            {activeTab === "address" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold text-gray-900">Saved Addresses</h3>
                  <button onClick={() => setShowAddressModal(true)} className="flex items-center gap-2 text-sm font-bold text-white bg-gray-900 px-5 py-2.5 rounded-xl hover:bg-gray-800 transition shadow-lg shadow-gray-200">
                    <FaPlus size={12} /> Add New
                  </button>
                </div>

                {addresses.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {addresses.map((addr, index) => (
                      <div key={index} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm relative group hover:border-blue-400 transition-colors">
                        <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button className="p-1.5 text-gray-400 hover:text-blue-600 bg-gray-50 rounded-md"><FaEdit size={12} /></button>
                          <button className="p-1.5 text-gray-400 hover:text-red-600 bg-gray-50 rounded-md"><FaTimes size={12} /></button>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-2 capitalize">{addr.city || "Address"}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {addr.flat_number}, {addr.street_name}<br />
                          {addr.land_mark && <>{addr.land_mark}, <br /></>}
                          {addr.city}, {addr.state} - <span className="font-semibold">{addr.pincode}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                    <FaMapMarkerAlt className="mx-auto text-gray-300 text-5xl mb-3" />
                    <p className="text-gray-500">No addresses saved yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- ADD ADDRESS MODAL --- */}
      {showAddressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-fade-in-up">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-lg text-gray-800">Add New Address</h3>
                    <button onClick={() => setShowAddressModal(false)} className="text-gray-400 hover:text-red-500 transition"><FaTimes size={20} /></button>
                </div>
                <div className="p-6">
                    <form onSubmit={handleSaveAddress}>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Flat / House No *</label><input type="text" name="flat_number" value={addressForm.flat_number} onChange={handleAddressInputChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none" placeholder="Flat 101"/></div>
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Pincode *</label><input type="text" name="pincode" value={addressForm.pincode} onChange={handleAddressInputChange} maxLength="6" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none" placeholder="500001"/></div>
                            </div>
                            <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Street / Area *</label><input type="text" name="street_name" value={addressForm.street_name} onChange={handleAddressInputChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none" placeholder="Main Road"/></div>
                            <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Landmark</label><input type="text" name="land_mark" value={addressForm.land_mark} onChange={handleAddressInputChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none" placeholder="Near Park"/></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">City *</label><input type="text" name="city" value={addressForm.city} onChange={handleAddressInputChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none" placeholder="City"/></div>
                                <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">State *</label><input type="text" name="state" value={addressForm.state} onChange={handleAddressInputChange} className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none" placeholder="State"/></div>
                            </div>
                        </div>
                        <div className="mt-8 flex gap-3">
                            <button type="button" onClick={() => setShowAddressModal(false)} className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-600 hover:bg-gray-50 transition">Cancel</button>
                            <button type="submit" disabled={addressLoading} className="flex-1 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition flex items-center justify-center gap-2">{addressLoading ? <FaSpinner className="animate-spin" /> : 'Save Address'}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
      )}

    </div>
  );
};

export default Profile;